import React, { useEffect, useCallback } from 'react';
import cx from 'clsx';

import { IconConnect } from '@/assets/swap';
import { HelperTooltip } from '@/components';
import { useUpdateChainQueryParam, useCurrentChain } from '@/hooks';
import { CHAINS_LIST } from '@/utils';

import style from './index.module.css';

interface ChainCheckableTagProps {
  onChange: (chain: string, connect?: boolean) => void;
  sort?: { [index: string]: number };
  value: string;
  notAll?: boolean;
  disableds?: string[];
  prefix?: string[];
  showConnect?: boolean;
  numberTip?: { [index: string]: number };
  className?: string;
  customChains?: Array<[string, string]>;
}

export const ChainCheckableTag = (props: ChainCheckableTagProps) => {
  const {
    notAll,
    showConnect,
    disableds = [],
    prefix,
    sort,
    className,
    onChange,
    value,
    customChains,
  } = props;

  const [chainid, setChain] = useUpdateChainQueryParam(value, notAll);
  const setChainUp = useCallback(
    (v: string, isConnect?: boolean) => {
      setChain(v);
      onChange?.(v, isConnect);
    },
    [setChain, onChange],
  );

  useEffect(() => {
    if (value !== undefined && chainid !== value) {
      setChainUp(value as string);
    }
    // eslint-disable-next-line
  }, [value, setChainUp]);

  const [chain] = useCurrentChain();

  const defaultV: Array<[string, string]> = customChains ?? [
    ['', 'All Chains'],
    ...CHAINS_LIST.map(v => [v.serverId, v.name] as [string, string]),
  ];
  if (prefix) {
    defaultV.unshift(...prefix.map(v => [v, v] as [string, string]));
  }
  const chains = new Map<any, string>(defaultV);
  if (notAll) {
    chains.delete('');
    // 没有默认值, 给 eth
    if (value === undefined && chainid === '') {
      setChainUp('eth');
      return null;
    }
  }

  if (prefix) {
    chains.set(prefix[0], prefix[0]);
  }

  // 如果当前已禁用, 切换成第一个
  if (disableds.indexOf(chainid) !== -1) {
    setChainUp([...chains].find(([id]) => disableds.indexOf(id) === -1)![0]);
  }
  const arr = Array.from(chains);
  const isNetLt0 = new Set();

  if (sort) {
    arr.sort((v1, v2) => {
      if (sort[v1[0]] === 0) {
        if (!disableds.includes(v1[0])) isNetLt0.add(v1[0]);
        return 1;
      }
      if (sort[v2[0]] === 0) {
        if (!disableds.includes(v2[0])) isNetLt0.add(v2[0]);
        return -1;
      }
      return 0;
    });
  }

  return (
    <div className={cx(style.searchItem, className)}>
      <div className={cx(style.items)}>
        {arr.map(([id, name]) => {
          const isDisabled = disableds.indexOf(id) !== -1 || isNetLt0.has(id);
          const isConnect = chain === id;
          const tipNumber = props.numberTip?.[id] ?? 0;
          const view = (
            <div
              key={`${id}_${name}`}
              onClick={() => {
                if (isDisabled) return;
                setChainUp(id, isConnect);
              }}
              className={cx(
                style.item,
                id === chainid ? style.active : '',
                isDisabled ? style.disabled : '',
              )}>
              {name}
              {isConnect && showConnect && (
                <img className={style.connectChain} src={IconConnect} alt="" />
              )}
              {tipNumber !== 0 && <span className={style.badge}>{tipNumber}</span>}
            </div>
          );
          return isDisabled ? (
            <HelperTooltip title={isNetLt0.has(id) ? 'No assets' : 'Coming soon'}>
              {view}
            </HelperTooltip>
          ) : (
            view
          );
        })}
      </div>
    </div>
  );
};
